/**
 * Sessions API service for communicating with the Hobbes backend
 * Provides session recording management and filtering capabilities
 */

import apiClient from './client';
import type { ApiResponse } from './client';

// Sales Analytics Types
export interface SalesSummary {
  session_recording_id: string;
  organization_id: string;
  email?: string | null;
  audio_recording_url?: string | null;
  primary_intent?: string | null;
  summary_markdown?: string | null;
  summary_plaintext?: string | null;
  qualification_status?: 'qualified' | 'unqualified' | 'unknown' | null;
  updated_qualification_status?: 'qualified' | 'unqualified' | 'unknown' | null;
  qualification_reason?: string | null;
  booked_status?: 'booked' | 'not_booked' | null;
  session_status?: 'active' | 'inactive' | null;
  qualification_stage?: 'lead' | 'mql' | 'sql' | 'opportunity' | null;
  budget_range?: string | null;
  timeline?: string | null;
  decision_maker_role?: string | null;
  is_decision_maker?: boolean | null;
  buying_intent?: 'low' | 'medium' | 'high' | 'none' | null;
  engagement_level?: 'disengaged' | 'neutral' | 'engaged' | 'highly_engaged' | null;
  sentiment?: 'positive' | 'neutral' | 'negative' | 'mixed' | null;
  confidence_score?: number | null;
  next_steps?: string[] | null;
  created_at?: string | null;
  started_at?: string | null;
  duration_seconds?: number | null;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  referrer_url?: string;
  landing_page_url?: string;
  device?: string;
  browser?: string;
  os?: string;
  user_agent?: string;
  geo_country?: string;
  geo_region?: string;
  geo_city?: string;
}

export interface ConversationMilestone {
  id: string;
  session_recording_id: string;
  sequence_order: number;
  milestone_type: 'introduction' | 'discovery' | 'demo' | 'objection' | 'pricing' | 'close';
  description: string;
  timestamp_offset_s: number;
  features_mentioned: string[];
  created_at: string;
}

export interface TopicDiscussed {
  id: string;
  session_recording_id: string;
  topic: string;
  interest_level: 'high' | 'medium' | 'low';
  time_discussed_s: number;
  outcome: 'needs_shown' | 'shown' | 'resolved' | 'blocker';
  created_at: string;
}

export interface Objection {
  id: string;
  session_recording_id: string;
  objection_type: 'price' | 'features' | 'competition' | 'timing' | 'integration' | 'trust';
  description: string;
  severity: 'blocker' | 'concern' | 'question';
  handled: boolean;
  resolution_notes: string;
  timestamp_offset_s: number;
  created_at: string;
}

export interface TranscriptEntry {
  id: string;
  speaker: string;
  text: string;
  timestamp: string;
  has_snapshot: boolean;
  sequence_number: number;
}

export interface SessionToolCall {
  id: string;
  tool_name: string;
  status: string;
  outputs_json: Record<string, any> | null;
  sequence: number;
  created_at: string | null;
}

export interface SalesAnalyticsResponse {
  sales_summary: SalesSummary;
  conversation_timeline: ConversationMilestone[];
  topics: TopicDiscussed[];
  objections: Objection[];
  transcripts: TranscriptEntry[];
  tool_calls: SessionToolCall[];
}

export interface SessionsResponse {
  sessions: SalesSummary[];
  total: number;
  limit: number;
  offset: number;
}


// Session-related types
class SessionsService {
  // Sales Analytics API Methods

  /**
   * Get all sessions for an organization (filtered + paginated)
   */
  async getOrganizationSessions(
    organizationId: string,
    filters?: {
      search?: string;
      qualification_status?: string;
      booked_status?: string;
      session_status?: string;
      date_from?: string;
      date_to?: string;
      limit?: number;
      offset?: number;
      target_session_id?: string;
    }
  ): Promise<ApiResponse<SessionsResponse>> {
    const query: Record<string, string> = {};

    if (filters?.search) {
      query.search = filters.search;
    }
    if (filters?.qualification_status) {
      query.qualification_status = filters.qualification_status;
    }
    if (filters?.booked_status) {
      query.booked_status = filters.booked_status;
    }
    if (filters?.session_status) {
      query.session_status = filters.session_status;
    }
    if (filters?.date_from) {
      query.date_from = filters.date_from;
    }
    if (filters?.date_to) {
      query.date_to = filters.date_to;
    }
    if (filters?.limit !== undefined) {
      query.limit = String(filters.limit);
    }
    if (filters?.offset !== undefined) {
      query.offset = String(filters.offset);
    }
    if (filters?.target_session_id) {
      query.target_session_id = filters.target_session_id;
    }

    return await apiClient.get<SessionsResponse>(
      `/api/sales-analytics/organizations/${organizationId}/sessions`,
      query
    );
  }

  /**
   * Get complete sales analytics for a session (summary + timeline + topics + objections)
   */
  async getSalesAnalytics(sessionId: string): Promise<ApiResponse<SalesAnalyticsResponse>> {
    return await apiClient.get<SalesAnalyticsResponse>(
      `/api/sales-analytics/sessions/${sessionId}`
    );
  }

  /**
   * Get sales summary only for a session
   */
  async getSalesSummary(sessionId: string): Promise<ApiResponse<{ sales_summary: SalesSummary }>> {
    return await apiClient.get<{ sales_summary: SalesSummary }>(
      `/api/sales-analytics/sessions/${sessionId}/summary`
    );
  }

  /**
   * Update qualification status for a session
   */
  async updateSessionQualificationStatus(
    sessionId: string,
    qualification_status: SalesSummary['qualification_status']
  ): Promise<ApiResponse<SalesSummary>> {
    return await apiClient.patch<SalesSummary>(
      `/api/sales-analytics/sessions/${sessionId}/qualification-status`,
      { qualification_status }
    );
  }

  /**
   * Get conversation timeline for a session
   */
  async getConversationTimeline(
    sessionId: string
  ): Promise<ApiResponse<{ conversation_timeline: ConversationMilestone[] }>> {
    return await apiClient.get<{ conversation_timeline: ConversationMilestone[] }>(
      `/api/sales-analytics/sessions/${sessionId}/timeline`
    );
  }

  /**
   * Get topics discussed in a session
   */
  async getTopicsDiscussed(
    sessionId: string,
    filters?: {
      interest_level?: 'high' | 'medium' | 'low';
      outcome?: 'needs_shown' | 'shown' | 'resolved' | 'blocker';
    }
  ): Promise<ApiResponse<{ topics: TopicDiscussed[] }>> {
    const query: Record<string, string> = {};

    if (filters?.interest_level) {
      query.interest_level = filters.interest_level;
    }
    if (filters?.outcome) {
      query.outcome = filters.outcome;
    }

    return await apiClient.get<{ topics: TopicDiscussed[] }>(
      `/api/sales-analytics/sessions/${sessionId}/topics`,
      query
    );
  }

  /**
   * Get objections raised in a session
   */
  async getObjections(
    sessionId: string,
    filters?: {
      objection_type?: 'price' | 'features' | 'competition' | 'timing' | 'integration' | 'trust';
      severity?: 'blocker' | 'concern' | 'question';
      handled?: boolean;
    }
  ): Promise<ApiResponse<{ objections: Objection[] }>> {
    const query: Record<string, string> = {};

    if (filters?.objection_type) {
      query.objection_type = filters.objection_type;
    }
    if (filters?.severity) {
      query.severity = filters.severity;
    }
    if (filters?.handled !== undefined) {
      query.handled = String(filters.handled);
    }

    return await apiClient.get<{ objections: Objection[] }>(
      `/api/sales-analytics/sessions/${sessionId}/objections`,
      query
    );
  }
}

// Create and export the service instance
const sessionsService = new SessionsService();
export default sessionsService;
